import { useState, FormEvent, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import { updatePageSEO, createBreadcrumbSchema, createPageSchema, cleanupDynamicSchemas } from '../../utils/seo';

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    // SEO設定
    updatePageSEO({
      title: 'お問い合わせ｜永山の勉強アーカイブ - ご質問・ご意見・ご要望',
      description: '永山の勉強アーカイブへのお問い合わせページ。サイトに関するご質問、ご意見、ご要望、誤りのご指摘などお気軽にお問い合わせください。学習内容の追加機能などのリクエストも受け付けています。',
      keywords: 'お問い合わせ,コンタクト,永山の勉強アーカイブ,ご質問,ご要望',
      canonical: 'https://nagayama-study-archive.readdy.ai/contact',
      ogType: 'website',
    });

    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: 'お問い合わせ', url: 'https://nagayama-study-archive.readdy.ai/contact' },
    ]);

    createPageSchema({
      name: 'お問い合わせ',
      description: 'サイトに関するご質問、ご意見、ご要望、誤りのご指摘などお気軽にお問い合わせください。',
      url: 'https://nagayama-study-archive.readdy.ai/contact',
      type: 'ContactPage',
    });

    return () => {
      cleanupDynamicSchemas();
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) return;

    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      await fetch('https://readdy.ai/api/form/d68pkm6a728k8ctu2d60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('送信エラー:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-600 dark:from-teal-800 dark:via-teal-700 dark:to-emerald-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-sm text-teal-100 dark:text-teal-200 mb-6">
            <a href="/" className="hover:text-white cursor-pointer transition-colors">Top</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">お問い合わせ</span>
          </nav>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              <strong>お問い合わせ</strong>
            </h1>
            <p className="text-teal-100 text-sm sm:text-base max-w-2xl">
              ご質問やご意見がございましたら、下記フォームよりお気軽にお問い合わせください。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <FadeInSection>
        <section className="py-10 sm:py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            {isSubmitted ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-8 sm:p-14 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 rounded-full mx-auto mb-6">
                  <i className="ri-check-double-line text-4xl text-teal-600 dark:text-teal-400"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">送信完了</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed text-sm sm:text-base">
                  お問い合わせいただきありがとうございます。<br />
                  内容を確認の上、必要に応じてご連絡いたします。
                </p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40 min-h-[44px]"
                >
                  <i className="ri-home-line"></i>
                  トップページへ戻る
                </a>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 sm:p-8">
                <div className="mb-6 flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-teal-200/30">
                    <i className="ri-mail-send-line text-base"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">お問い合わせフォーム</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      サイトに関するご質問、ご意見、誤りのご指摘などお気軽にどうぞ。
                    </p>
                  </div>
                </div>

                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-5"
                >
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                      お名前 <span className="text-gray-400 font-normal">（任意）</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white transition-shadow min-h-[44px]"
                      placeholder="山田 太郎"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                      メールアドレス <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white transition-shadow min-h-[44px]"
                      placeholder="example@email.com"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                      件名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white transition-shadow min-h-[44px]"
                      placeholder="お問い合わせの件名"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
                      お問い合わせ内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 dark:text-white transition-shadow"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">500文字以内</p>
                  </div>

                  {/* Agreement */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="agreement"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 w-5 h-5 text-teal-600 border-gray-300 dark:border-gray-600 rounded focus:ring-teal-500 cursor-pointer shrink-0"
                    />
                    <label htmlFor="agreement" className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer leading-relaxed">
                      <a href="/privacy" className="text-teal-600 dark:text-teal-400 hover:underline">プライバシーポリシー</a>に同意の上、送信します。
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={!agreed || isSubmitting}
                      className={`w-full px-8 py-3.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap flex items-center justify-center gap-2 min-h-[48px] ${
                        agreed && !isSubmitting
                          ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:from-teal-600 hover:to-emerald-700 cursor-pointer shadow-md shadow-teal-200/40'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          送信中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-base"></i>
                          送信する
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
      </FadeInSection>

      <Footer />
    </div>
  );
}
